import { useCallback, useEffect, useState } from 'react';
import { startOfWeek } from 'date-fns';
import axios from 'axios';
import { getToken } from './utils';
import { getApiUrl } from '../../utils/apiUtils';
import { debugError } from '../../utils/debug';

const WEEK_DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const SWAP_TIMEOUT_MS = 30000;

const getWeekStartMonday = (date) => startOfWeek(date, { weekStartsOn: 1 });

const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseAxiosError = (error) => {
  if (error.response?.data?.detail) {
    if (typeof error.response.data.detail === 'string') {
      return error.response.data.detail;
    }
    if (Array.isArray(error.response.data.detail)) {
      return error.response.data.detail.map((item) => item.msg || item).join(', ');
    }
    return JSON.stringify(error.response.data.detail);
  }
  return error.message || 'Unexpected error';
};

/**
 * Manages all state and logic for drag-and-drop swap/move functionality.
 * @param {Object} params
 * @param {Object} params.user - Current authenticated user object.
 * @param {boolean} params.isSwapEnabled - Flag to enable/disable swap interaction.
 * @param {Function} params.fetchRegimensData - Function to refresh regimen data after a successful swap.
 * @returns {Object} State and handlers for swap workflow.
 */
const useSwapManager = ({ user, isSwapEnabled, fetchRegimensData }) => {
  const [pendingSwap, setPendingSwap] = useState(null);
  const [swapStatus, setSwapStatus] = useState({ loading: false, error: null });
  const [swapFeedback, setSwapFeedback] = useState(null);

  useEffect(() => {
    if (!swapFeedback?.message) return;
    const timeout = setTimeout(() => setSwapFeedback(null), 3500);
    return () => clearTimeout(timeout);
  }, [swapFeedback]);

  const handleSwapCancel = useCallback(() => {
    if (swapStatus.loading) {
      return;
    }
    setPendingSwap(null);
    setSwapStatus({ loading: false, error: null });
    setSwapFeedback(null);
  }, [swapStatus.loading]);

  const handleSwapRequest = useCallback(({ source, target }) => {
    if (!isSwapEnabled) return;

    if (!source?.run || !target) return;

    const sourceDate = new Date(source.date);
    const targetDate = new Date(target.date);
    const sourceWeek = formatDateLocal(getWeekStartMonday(sourceDate));
    const targetWeek = formatDateLocal(getWeekStartMonday(targetDate));

    // 🚨 CROSS-WEEK SWAP RESTRICTION: Intentionally blocked — high data-loss risk.
    // Cross-week swaps require accessing two different RegimensWeekly rows (different database records),
    // which the current architecture does not safely support. This is planned as a future feature.
    // See: CROSS_WEEK_SWAP_ANALYSIS.md for technical details.
    if (sourceWeek !== targetWeek) {
      setSwapFeedback({
        message: 'Moving workouts between different weeks is not supported yet.',
        targetDate: target?.date ? new Date(target.date).toISOString() : source?.date ? new Date(source.date).toISOString() : null,
        variant: 'warning'
      });
      return;
    }

    if (target.run && source.run.id === target.run.id) {
      return;
    }

    if (source.run.status === 'completed' || target.run?.status === 'completed') {
      setSwapFeedback({
        message: 'Moving completed workouts is not supported yet.',
        targetDate: target?.date ? new Date(target.date).toISOString() : source?.date ? new Date(source.date).toISOString() : null,
        variant: 'warning'
      });
      return;
    }

    const mode = target.run && target.run.status !== 'deleted' ? 'swap' : 'move';
    setSwapStatus({ loading: false, error: null });
    setPendingSwap({ source, target, mode });
  }, [isSwapEnabled]);

  const performSwap = useCallback(async () => {
    if (!pendingSwap) return;

    const { source, target, mode } = pendingSwap;

    setSwapStatus({ loading: true, error: null });

    try {
      const token = await getToken(user);
      const API_URL = getApiUrl();
      const headers = { Authorization: `Bearer ${token}` };

      const sourceDayKey = WEEK_DAY_KEYS[source.run.day_index];
      const targetDayKey = WEEK_DAY_KEYS[target.dayIndex];

      if (mode === 'move') {
        await axios.put(
          `${API_URL}/api/regimens/weekly/${source.run.regimen_id}`,
          {
            [targetDayKey]: {
              title: source.run.title,
              subtitle: source.run.subtitle ?? null,
              details: source.run.details ?? null
            }
          },
          { headers, timeout: SWAP_TIMEOUT_MS }
        );

        await axios.put(
          `${API_URL}/api/regimens/weekly/${source.run.regimen_id}`,
          {
            delete_days: [sourceDayKey]
          },
          { headers, timeout: SWAP_TIMEOUT_MS }
        );

        setSwapFeedback({
          message: 'Workout moved to the selected day.',
          targetDate: target?.date ? new Date(target.date).toISOString() : null,
          sourceDate: source?.date ? new Date(source.date).toISOString() : null,
          variant: 'success'
        });
      } else {
        // 🔄 DEEP SWAP: Include all workout metadata to preserve user experience
        await axios.put(
          `${API_URL}/api/regimens/weekly/${source.run.regimen_id}`,
          {
            [sourceDayKey]: {
              title: target.run.title,
              subtitle: target.run.subtitle ?? null,
              details: target.run.details ?? null,
              enhancement_status: target.run.enhancement_status ?? null,
              completion_status: target.run.status ?? null,
              completion_details: target.run.completion_details ?? null
            },
            [targetDayKey]: {
              title: source.run.title,
              subtitle: source.run.subtitle ?? null,
              details: source.run.details ?? null,
              enhancement_status: source.run.enhancement_status ?? null,
              completion_status: source.run.status ?? null,
              completion_details: source.run.completion_details ?? null
            }
          },
          { headers, timeout: SWAP_TIMEOUT_MS }
        );

        // Get day names for success message
        const sourceDayName = sourceDayKey.charAt(0).toUpperCase() + sourceDayKey.slice(1);
        const targetDayName = targetDayKey.charAt(0).toUpperCase() + targetDayKey.slice(1);

        setSwapFeedback({
          message: `Swapped your full workouts — including every detail, pacing note, and your personal feedback — between ${sourceDayName} and ${targetDayName}. Nothing was regenerated. Everything moved exactly as it was.`,
          targetDate: target?.date ? new Date(target.date).toISOString() : null,
          sourceDate: source?.date ? new Date(source.date).toISOString() : null,
          variant: 'success'
        });
      }

      setSwapStatus({ loading: false, error: null });
      await fetchRegimensData(true);
      setPendingSwap(null);
    } catch (error) {
      debugError('Error swapping workouts:', error);

      const isTimeoutError = error?.code === 'ECONNABORTED' || error?.message?.toLowerCase()?.includes('timeout');
      const timeoutMessage = 'Swap request timed out. Please check your connection and try again.';
      const errorMessage = isTimeoutError ? timeoutMessage : parseAxiosError(error);

      setSwapStatus({ loading: false, error: errorMessage });

      setSwapFeedback({
        message: errorMessage,
        targetDate: target?.date ? new Date(target.date).toISOString() : source?.date ? new Date(source.date).toISOString() : null,
        sourceDate: source?.date ? new Date(source.date).toISOString() : null,
        variant: isTimeoutError ? 'warning' : 'error'
      });
    }
  }, [pendingSwap, user, fetchRegimensData]);

  return {
    pendingSwap,
    swapStatus,
    swapFeedback,
    handleSwapRequest,
    handleSwapCancel,
    performSwap
  };
};

export default useSwapManager;
